import React, { useEffect, useRef, useState } from 'react';
import { EyeShowIcon, EyeHideIcon, HDCameraIcon } from '@rsos/assets/icons';
import AttentionTriangleIcon from '@rsos/assets/icons/src/AttentionTriangleIcon';
import { Box, Text } from '@rsos/base-components';
import Loader from '@rsos/components/capstone/Loader';
import { toggleFullscreen } from '@rsos/utils/fullscreenHelpers';
import { EAGLE_EYE_VIDEO } from '../constants';
import EagleEyePlayer from './EagleEyePlayer';
import {
  IconBar,
  Circle,
  IconButton,
  Preview,
  BlurStopper,
} from './EagleEyeVideo.styles';
import { fetchEENCookie, usePreviewEventListeners } from './helpers';
import { trackBlurPreview, trackFullscreen } from './trackings';

/**
 * Renders custom media playback for EEN using their multipart preview endpoint and SDK
 */
const EagleEyeVideo = ({ data }) => {
  const { baseUrl, id, previewUrl, token } =
    data.camera_information[EAGLE_EYE_VIDEO];

  const fullscreenRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isBlurred, setIsBlurred] = useState(true);
  const [imageUrl, setImageUrl] = useState(null);
  const [isError, setIsError] = useState(false);
  const previewRef = useRef(null);

  const updateUrl = () => {
    setImageUrl(previewUrl);
  };

  const handleBlur = () => {
    trackBlurPreview(isBlurred, id);
    setIsBlurred(prev => !prev);
  };

  const handleFullscreen = () => {
    trackFullscreen(id);
    toggleFullscreen(fullscreenRef.current);
  };

  useEffect(() => {
    fetchEENCookie(baseUrl, token, id, setIsLoading, setIsError, updateUrl);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  usePreviewEventListeners(previewRef, setIsError, imageUrl, id, setIsLoading);

  return (
    <Box marginX="16px">
      {isLoading && <Loader noText fluid />}
      <Box position="relative">
        {isError ? (
          <Box
            bg="lightDanger"
            p="12px 16px"
            horizontal
            align="center"
            borderRadius="5px"
          >
            <AttentionTriangleIcon color="background" height="16" width="16" />
            <Text color="background" size="small" ml="8px">
              Video failed to load. Please close and try again.
            </Text>
          </Box>
        ) : (
          <>
            <BlurStopper height={isLoading ? '0' : 'auto'}>
              <Preview
                isBlurred={isBlurred}
                src={imageUrl}
                ref={previewRef}
                width="100%"
              />
            </BlurStopper>
            <IconBar isHidden={isLoading}>
              <Box horizontal align="center">
                <Circle />
                <Text color="white" size="xsmall" ml="4px">
                  LIVE
                </Text>
              </Box>
              <Box horizontal align="center">
                <IconButton onClick={handleBlur}>
                  {isBlurred ? (
                    <EyeHideIcon height="16px" width="16px" />
                  ) : (
                    <EyeShowIcon height="16px" width="16px" />
                  )}
                </IconButton>
                <IconButton disabled={isBlurred} onClick={handleFullscreen}>
                  <HDCameraIcon height="16px" width="16px" />
                </IconButton>
              </Box>
            </IconBar>
            <EagleEyePlayer
              token={token}
              baseUrl={baseUrl}
              cameraId={id}
              fullscreenRef={fullscreenRef}
            />
          </>
        )}
      </Box>
    </Box>
  );
};

export default EagleEyeVideo;
